import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Search, UserPlus, Users, Printer, Copy, Check } from "lucide-react";
import { staffApi, apiError } from "../lib/api";

export default function ChoristersPage() {
  const [rows, setRows] = useState(null);
  const [groups, setGroups] = useState([]);
  const [q, setQ] = useState("");
  const [groupFilter, setGroupFilter] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [bulkNames, setBulkNames] = useState("");
  const [grade, setGrade] = useState(1);
  const [selGroups, setSelGroups] = useState([]);
  const [created, setCreated] = useState([]);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState("");

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (groupFilter) params.set("group_id", groupFilter);
    staffApi.get(`/staff/choristers?${params}`).then((r) => setRows(r.data.choristers));
  }, [q, groupFilter]);

  useEffect(() => { staffApi.get("/staff/groups").then((r) => setGroups(r.data.groups)); }, []);
  useEffect(() => { load(); }, [groupFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleGroup = (id) => setSelGroups((s) => (s.includes(id) ? s.filter((g) => g !== id) : [...s, id]));

  const submit = async (bulk) => {
    setError(""); setCreated([]);
    try {
      if (bulk) {
        const names = bulkNames.split("\n").map((n) => n.trim()).filter(Boolean);
        const { data } = await staffApi.post("/staff/choristers/bulk", { names, grade, group_ids: selGroups });
        setCreated(data.created);
      } else {
        const names = bulkNames.split("\n").map((n) => n.trim()).filter(Boolean);
        const { data } = await staffApi.post("/staff/choristers", { display_name: names[0] || "", grade, group_ids: selGroups });
        setCreated([data.created]);
      }
      load();
    } catch (e) {
      setError(apiError(e));
    }
  };

  const copyCard = (c) => {
    navigator.clipboard.writeText(`ChoirQuest login — Institution: DEMOCHOIR (see card)\nUsername: ${c.username}\nPIN: ${c.pin}`);
    setCopied(c.username);
    setTimeout(() => setCopied(""), 1500);
  };

  return (
    <div className="space-y-6" data-testid="choristers-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#0B192C]">Choristers</h1>
        <div className="flex gap-2">
          <Link to="/staff/login-cards" data-testid="login-cards-link"
            className="inline-flex items-center gap-2 px-4 py-3 rounded-xl border-2 border-[#0B192C] text-[#0B192C] font-bold text-sm hover:bg-[#0B192C] hover:text-[#FAF7EE] transition-colors min-h-[48px]">
            <Printer className="w-4 h-4" /> Login cards
          </Link>
          <button onClick={() => setShowCreate(true)} data-testid="add-chorister-btn"
            className="inline-flex items-center gap-2 px-4 py-3 rounded-xl bg-[#C69B3D] text-[#0B192C] font-bold text-sm hover:bg-[#D8AB49] transition-colors min-h-[48px]">
            <UserPlus className="w-4 h-4" /> Add choristers
          </button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#566270]" aria-hidden />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search name or username" data-testid="chorister-search"
            className="w-full rounded-xl border-2 border-[#E5DEC9] bg-white pl-11 pr-4 py-3 focus:border-[#C69B3D] focus:outline-none min-h-[48px]" />
        </div>
        <select value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)} data-testid="group-filter"
          className="rounded-xl border-2 border-[#E5DEC9] bg-white px-4 py-3 focus:border-[#C69B3D] focus:outline-none min-h-[48px]">
          <option value="">All groups</option>
          {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
        <button onClick={load} data-testid="search-btn"
          className="px-5 py-3 rounded-xl bg-[#0B192C] text-[#FAF7EE] font-bold text-sm min-h-[48px]">Search</button>
      </div>

      <div className="rounded-2xl bg-white border border-[#E5DEC9] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-[#566270] bg-[#FAF7EE] border-b border-[#E5DEC9]">
                <th className="px-5 py-3">Name</th><th className="px-5 py-3">Username</th><th className="px-5 py-3">Group</th>
                <th className="px-5 py-3">Grade</th><th className="px-5 py-3">Notes</th><th className="px-5 py-3">Streak</th>
                <th className="px-5 py-3">Mastery</th><th className="px-5 py-3">Last active</th>
              </tr>
            </thead>
            <tbody>
              {(rows || []).map((r) => (
                <tr key={r.id} className="border-b border-[#F3EFE0] hover:bg-[#FAF7EE] transition-colors" data-testid={`chorister-row-${r.id}`}>
                  <td className="px-5 py-3">
                    <Link to={`/staff/choristers/${r.id}`} className="font-bold text-[#0B192C] hover:text-[#7A1C2E]" data-testid={`chorister-link-${r.id}`}>
                      {r.display_name}
                    </Link>
                  </td>
                  <td className="px-5 py-3 font-note text-[#566270]">{r.username}</td>
                  <td className="px-5 py-3 text-[#1C242B]">{r.groups.join(", ") || "—"}</td>
                  <td className="px-5 py-3">Grade {r.grade}</td>
                  <td className="px-5 py-3 font-bold text-[#8a6a10]">{r.notes_total.toLocaleString()}</td>
                  <td className="px-5 py-3">{r.streak}d</td>
                  <td className="px-5 py-3">{r.mastery_avg}%</td>
                  <td className="px-5 py-3 text-[#566270]">{r.last_active ? new Date(r.last_active).toLocaleDateString("en-GB") : "Never"}</td>
                </tr>
              ))}
              {rows && rows.length === 0 && (
                <tr><td colSpan={8} className="px-5 py-8 text-center text-[#566270]">
                  <Users className="w-6 h-6 mx-auto mb-2 text-[#E5DEC9]" />No choristers found.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showCreate && (
        <div className="fixed inset-0 bg-[#0B192C]/70 flex items-center justify-center p-4 z-50" role="dialog" aria-modal="true" data-testid="create-dialog">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="font-display text-xl font-bold text-[#0B192C]">Add choristers</h2>
            <p className="text-sm text-[#566270] mt-1">One name per line for bulk creation. Usernames and PINs are generated automatically — note them down, PINs are shown only once.</p>
            <textarea value={bulkNames} onChange={(e) => setBulkNames(e.target.value)} rows={5}
              placeholder={"Emily\nJames\nSophia"} data-testid="bulk-names-input"
              className="mt-4 w-full rounded-xl border-2 border-[#E5DEC9] px-4 py-3 focus:border-[#C69B3D] focus:outline-none" />
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-[#566270]" htmlFor="new-grade">Starting grade</label>
                <select id="new-grade" value={grade} onChange={(e) => setGrade(Number(e.target.value))} data-testid="new-grade-select"
                  className="mt-1 w-full rounded-xl border-2 border-[#E5DEC9] px-3 py-3 bg-white min-h-[48px]">
                  {[1, 2, 3, 4, 5].map((g) => <option key={g} value={g}>Grade {g}</option>)}
                </select>
              </div>
              <div>
                <span className="text-xs font-bold uppercase tracking-widest text-[#566270]">Group</span>
                <div className="mt-1 space-y-1">
                  {groups.map((g) => (
                    <label key={g.id} className="flex items-center gap-2 text-sm min-h-[36px]">
                      <input type="checkbox" checked={selGroups.includes(g.id)} onChange={() => toggleGroup(g.id)}
                        data-testid={`new-group-${g.id}`} className="w-5 h-5 accent-[#C69B3D]" />
                      {g.name}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            {error && <p role="alert" className="text-sm text-[#A62B2B] mt-3" data-testid="create-error">{error}</p>}
            {created.length > 0 && (
              <div className="mt-4 rounded-xl bg-[#EBF6F0] border border-[#2D6A4F]/40 p-4" data-testid="created-list">
                <p className="text-xs font-bold uppercase tracking-widest text-[#2D6A4F] mb-2">Created — record these PINs now</p>
                {created.map((c) => (
                  <div key={c.id} className="flex items-center justify-between text-sm py-1">
                    <span className="font-semibold text-[#0B192C]">{c.display_name}</span>
                    <span className="font-note text-[#566270]">{c.username}</span>
                    <span className="font-note font-bold text-[#7A1C2E]">{c.pin}</span>
                    <button onClick={() => copyCard(c)} aria-label="Copy login" data-testid={`copy-${c.username}`} className="p-1.5">
                      {copied === c.username ? <Check className="w-4 h-4 text-[#2D6A4F]" /> : <Copy className="w-4 h-4 text-[#566270]" />}
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2 mt-5">
              <button onClick={() => submit(bulkNames.includes("\n"))} data-testid="create-submit"
                className="flex-1 px-4 py-3 rounded-xl bg-[#C69B3D] text-[#0B192C] font-bold min-h-[48px]">
                Create accounts
              </button>
              <button onClick={() => { setShowCreate(false); setCreated([]); setBulkNames(""); setError(""); }} data-testid="create-cancel"
                className="px-4 py-3 rounded-xl border-2 border-[#E5DEC9] font-bold min-h-[48px]">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
